// Static editor runtime, shared by App (which calls setAuthTokenProvider at init) and the lazy
// editors chunk (which uses uploadFile/resolveSrc in createEditor). Kept OUT of editors.tsx so that
// React.lazy-ing the editors doesn't drag this init-time code — or the editor/CRDT chunk — into the
// main/login bundle (audit F1b).
// Deep imports (NOT the `@lapo/ui` barrel) so this init-time module — statically imported by App —
// doesn't drag the barrel's tiptap/prosemirror editor runtime into the main/login bundle (PERF-001 /
// audit F1b). signedUrls.ts and files.ts are import-free, so these stay editor-runtime-free.
import { createSignedUrlResolver, type SignedUrlResolver } from '@lapo/ui/editor/signedUrls';
import { type UploadFn } from '@lapo/ui/files';
import { API_URL, TOKEN } from './config.js';

// Live auth token for upload/sign requests. uploadFile/resolveSrc are built once at module scope, so
// they read the token through this holder; the app points it at the live Firebase token once signed
// in (the static env TOKEN is only a dev fallback). Without this, signed-URL fetches 401 and every
// image/file renders broken.
let getAuthToken: () => Promise<string | null> = async () => TOKEN || null;
export function setAuthTokenProvider(fn: () => Promise<string | null>): void {
  getAuthToken = fn;
}
async function authHeaders(): Promise<Record<string, string>> {
  const t = await getAuthToken();
  return t ? { authorization: `Bearer ${t}` } : {};
}

/**
 * File support (IMPL-17). When the cloud API is configured, uploads POST raw bytes to `/files` and
 * get back a stable `lapo://` reference stored in the doc; the resolver mints short-lived signed
 * read URLs from `/files/sign` for the image/file node views. Built once and shared across editors.
 * With VITE_API_URL unset, both are left undefined and createEditor falls back to embedding data
 * URLs — drag-drop/paste of images & files still works fully offline.
 */
export const uploadFile: UploadFn | undefined = API_URL
  ? async (file) => {
      const res = await fetch(`${API_URL}/files`, {
        method: 'POST',
        headers: {
          ...(await authHeaders()),
          'content-type': file.type || 'application/octet-stream',
          'x-filename': encodeURIComponent(file.name),
        },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      return res.json();
    }
  : undefined;

export const resolveSrc: SignedUrlResolver | undefined = API_URL
  ? createSignedUrlResolver({
      fetchSigned: async (uri) => {
        const res = await fetch(`${API_URL}/files/sign?uri=${encodeURIComponent(uri)}`, {
          headers: await authHeaders(),
        });
        if (!res.ok) throw new Error(`sign failed: ${res.status}`);
        return res.json();
      },
    })
  : undefined;
